<template>
  <SectionWrapper :class="editMode ? 'editmode' : 'viewmode'">
    <FileUpload
      ref="fileUploadRef"
      mode="advanced"
      :multiple="true"
      name="files"
      :accept="allowedFileTypes"
      :auto="false"
      :showUploadButton="false"
      :showCancelButton="false"
      :customUpload="true"
      class="p-fluid"
      @select="onFileSelect"
    >
      <template #header="{ chooseCallback, uploadCallback, clearCallback, files }">
        <div
          v-if="!props.hideButtons"
          class="flex flex-wrap justify-between items-center flex-1 gap-4"
        >
          <div class="flex gap-2">
            <Button
              icon="pi pi-images"
              rounded
              outlined
              severity="secondary"
              @click="chooseCallback()"
            ></Button>
            <Button
              v-if="fileFields.length && !allUploadsDoneOrError"
              icon="pi pi-cloud-upload"
              rounded
              outlined
              severity="success"
              :disabled="!files || files.length === 0"
              @click="uploadEvent(() => uploadCallback())"
            ></Button>
            <Button
              v-if="fileFields.length && !allUploadsDone"
              icon="pi pi-times"
              rounded
              outlined
              severity="danger"
              :disabled="!files || files.length === 0"
              @click="
                () => {
                  clearUploadedFiles();
                  clearCallback();
                }
              "
            ></Button>
          </div>
        </div>
        <div v-else>&nbsp;</div>
      </template>
      <template #content>
        <div
          v-if="!pendingFiles.length"
          v-tooltip="{ value: t('titletags.chooseFiles') }"
          class="dropzone cursor-pointer"
          @click="chooseFiles()"
        >
          <i class="pi pi-cloud-upload !rounded-full !p-8 !text-4xl !text-muted-color" />
          <p class="mt-6 mb-0">{{ t('pages.documentsDetail.uploadExplanation') }}</p>
        </div>
        <div :class="uploading ? 'visible' : 'hidden'">
          <ProgressBar :value="totalSizePercent" :showxValue="false">
            <span class="whitespace-nowrap">{{ totalSize }}B / 1Mb</span>
          </ProgressBar>
        </div>
        <Fieldset
          v-if="
            allowClassificationChange &&
            pendingFiles.filter((file) => file.value.status !== 'error').length > 1
          "
          :legend="t('pages.documentsDetail.batchClassification')"
        >
          <TicketSelection
            :model="classificationModel"
            namePrefix="batch"
            :fetchPointOfSalesData="fetchPointOfSalesForLazySelect"
            :fetchTicketsData="fetchTicketsForLazySelect"
            :fetchSinglePointOfSale="fetchSinglePointOfSaleById"
            :fetchSingleTicket="fetchSingleTicketById"
            :initialData="{
              pointOfSaleId: classificationModel.pointOfSaleId,
              documentType: classificationModel.documentType,
              ticketId: classificationModel.ticketId,
            }"
            :allowClassificationPrefilled="allowClassificationChange"
            @update:pointOfSaleId="
              (v: string | undefined) => (classificationModel.pointOfSaleId = v)
            "
            @update:documentType="
              (v: TicketDocumentV1Dto.documentType | undefined) =>
                (classificationModel.documentType = v)
            "
            @update:ticketId="onBatchTicketIdUpdate"
          />
          <FormTextArea v-model="classificationModel.comment" name="comment" compact />
        </Fieldset>

        <form v-if="pendingFiles.length && props.showUploadedList">
          <ul v-if="fileFields.length" class="list-none p-0 m-0">
            <li
              v-for="(field, index) in fileFields.filter((ff) => ff.value.status !== 'done')"
              :key="field.key"
            >
              <div class="flex items-center justify-between">
                <div class="flex flex-col w-full gap-4">
                  <div>
                    <h3>{{ field.value?.file.name }}</h3>
                    <NotificationBar
                      v-if="field.value?.response?.error || field.value.isEncrypted"
                      :mainText="
                        t(
                          `toast.errors.${field.value.isEncrypted ? 'encrypted' : field.value?.response?.error}` ||
                            '',
                        )
                      "
                      severity="error"
                      :showCloseButton="false"
                    />
                  </div>
                  <div v-if="!field.value?.response?.error && !field.value.isEncrypted">
                    <div class="flex items-center gap-3">
                      <TicketSelection
                        :key="`files.${index}.ticketSelection-${ticketsAlreadyExisting.length}`"
                        :model="field.value"
                        :globalDefault="classificationModel"
                        :namePrefix="`files.${index}`"
                        :formPrefix="`files.${index}`"
                        :fetchPointOfSalesData="getFileFetchPointOfSales(index)"
                        :fetchTicketsData="fetchTicketsForLazySelect"
                        :fetchSinglePointOfSale="fetchSinglePointOfSaleById"
                        :fetchSingleTicket="fetchSingleTicketById"
                        :initialData="{
                          pointOfSaleId: field.value.pointOfSaleId,
                          documentType: field.value.documentType,
                          ticketId: field.value.ticketId,
                        }"
                        :allowClassificationPrefilled="allowClassificationChange"
                        @update:pointOfSaleId="(v) => onPointOfSaleUpdate(v, field)"
                        @update:documentType="
                          (v: TicketDocumentV1Dto.documentType | undefined) =>
                            onDocumentTypeUpdate(v, field)
                        "
                        @update:ticketId="(v: string | undefined) => onTicketIdUpdate(v, field)"
                        @update-user:ticketId="(e) => onTicketIdUserUpdate(e, index, field)"
                      />
                      <div v-if="allowClassificationChange">
                        <FormCheckbox
                          :key="`files.${index}.isNewTicket`"
                          v-model="field.value.isNewTicket"
                          label="isNewTicket"
                          :name="`files.${index}.isNewTicket`"
                          :initialValue="false"
                          :disabled="!editMode"
                          :isDirty="() => false"
                          @update:modelValue="(v: boolean) => onNewTicketCheck(v, index, field)"
                        />
                      </div>
                    </div>
                    <FormTextArea
                      v-model="field.value.comment"
                      :label="t('comment')"
                      :name="`files.${index}.comment`"
                      compact
                    />
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </form>
      </template>
      <template #empty> </template>
    </FileUpload>

    <div v-if="fileFields.length && !!props.showUploadedList">
      <ul class="list-none p-0 m-0 my-8">
        <li
          v-for="(fileObj, index) in fileFields.filter((f) => f.value.status === 'done')"
          :key="index"
          class="mb-2"
        >
          <div v-if="(uploading && fileObj.value.response) || fileObj.value.status === 'error'">
            <div class="flex items-center justify-between">
              <span>{{ fileObj.value.file.name }}</span>
            </div>
            <div :class="uploading ? 'visible' : 'hidden'">
              <ProgressBar
                class="md:w-20rem h-2 w-full md:ml-auto"
                :value="fileObj.value.progress"
                :showValue="false"
              />
            </div>
          </div>
        </li>
      </ul>

      <div v-if="fileFields.filter((f) => f.value.status === 'done')?.length">
        <h3>{{ t('pages.documentsDetail.uploadedHeadline') }}</h3>
        <DataTable
          data-key="uploadedDocuments"
          :data="fileFields.filter((f) => f.value.status === 'done').map((f) => f.value.response)"
          :columns="uploadColumns"
          :reorderableRows="false"
          :tableActions="false"
          :showPrimaryToolbar="false"
          :showSecondaryToolbarButtons="false"
          :showSecondaryToolbar="false"
          :onRowClick="() => {}"
          :onRowDblClick="() => {}"
          :hasExpandedRow="true"
          :checkboxMultiselect="false"
        >
          <template #custom-expanded-row="{ row }">
            <FileViewer
              :uuid="row.documentId._meta.rawvalue"
              :mime="row.mimeType?._meta.rawvalue ?? 'application/pdf'"
              :filename="row.name._meta.rawvalue"
            />
          </template>
        </DataTable>
      </div>

      <p v-if="uploading" class="mt-4 text-sm text-muted">
        <span class="pi pi-spin pi-spinner"></span>
        {{ t('pages.documentsDetail.uploadInProgressMessage') }}
      </p>
    </div>
  </SectionWrapper>
</template>

<script setup lang="ts">
import { computed, type Ref, ref, watch } from 'vue';
import type { SelectChangeEvent } from 'primevue';
import ProgressBar from 'primevue/progressbar';
import { type FieldEntry, useFieldArray, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import { useDetailNavigation } from '@/components/details/hooks/useDetailNavigation';
import TicketSelection from '@/views/classification/components/TicketSelection.vue';
import { useClassificationLookups } from '@/views/classification/hooks/useClassificationLookups';
import { getAllowedFileTypes, isPdfEncrypted } from '@/utils/files';
import type { DocumentV1Dto, TicketDocumentV1Dto } from '@/openapi';
import { eventBus } from '@/configs/eventBus';

import { uploadColumns } from '../configs/documentColumnConfig';
import { getUploadDocumentSchema } from '../configs/documentFormConfig';
import type { DefaultClassification, UploadFileObject } from '../documentsTypes';
import { useDocuments } from '../hooks/useDocuments';

const { t } = useI18n();
const { uploadDocument } = useDocuments();
const { editMode } = useDetailNavigation();
const form = useForm<{ files: UploadFileObject[] }>({
  validationSchema: getUploadDocumentSchema(t),
  initialValues: {
    files: [],
  },
});

const fieldArray = useFieldArray<UploadFileObject>('files');
const fileFields = fieldArray.fields;

const { values } = form;
const allowedFileTypes = getAllowedFileTypes([
  'pdf',
  'jpg',
  'jpeg',
  'png',
  'svg',
  'xls',
  'xlsx',
  'csv',
  'doc',
  'docx',
]);

const uploading = ref(false);
const totalUploaded = ref(0); // hochgeladene Bytes
const totalSize = ref(0); // in Bytes

// Track which files have been manually modified by the user
// This prevents batch classification from overwriting user's individual choices
// Structure: { [fileIndex]: { pointOfSaleId?: boolean, ticketId?: boolean, documentType?: boolean } }
const touched = ref<
  Record<number, { pointOfSaleId?: boolean; ticketId?: boolean; documentType?: boolean }>
>({});

const {
  ticketsAlreadyExisting,
  generateTempTicket,
  removeTempTicket,
  createFetchPointOfSalesForLazySelect,
  fetchTicketsForLazySelect,
  fetchSinglePointOfSaleById,
  fetchSingleTicketById,
} = useClassificationLookups();

// Batch classification uses no ticket filter (shows all POS)
const fetchPointOfSalesForLazySelect = computed(() =>
  createFetchPointOfSalesForLazySelect(undefined),
);

// Cache of fetch functions per ticketId to avoid recreating on every render
const fetchFunctionsCache = new Map<
  string | undefined,
  ReturnType<typeof createFetchPointOfSalesForLazySelect>
>();

// Get stable fetch function for a specific file based on its ticketId
const getFileFetchPointOfSales = (index: number) => {
  const ticketId = fileFields.value[index]?.value?.ticketId ?? undefined;
  const cacheKey = ticketId ?? 'undefined';

  if (!fetchFunctionsCache.has(cacheKey)) {
    fetchFunctionsCache.set(cacheKey, createFetchPointOfSalesForLazySelect(ticketId));
  }

  return fetchFunctionsCache.get(cacheKey)!;
};

const props = withDefaults(
  defineProps<{
    hideButtons?: boolean;
    defaultClassification?: DefaultClassification;
    allowClassificationChange?: boolean;
    showUploadedList?: boolean;
  }>(),
  {
    defaultClassification: () => ({
      pointOfSaleId: undefined,
      documentType: undefined,
      ticketId: undefined,
      isNewTicket: false,
      isClassificationNotApplicable: false,
      comment: '',
    }),
    allowClassificationChange: true,
    showUploadedList: true,
  },
);

const emit = defineEmits<{
  (e: 'uploaded', payload: { ids: string[]; documents: DocumentV1Dto[] }): void;
  (e: 'error', payload: { message: string }): void;
}>();

const allowClassificationChange = computed(() => props.allowClassificationChange);

const pendingFiles = computed(() => {
  return fileFields.value.filter(
    (file) => file.value.status === 'pending' || file.value.status === 'error',
  );
});

const allUploadsDone = computed(() =>
  fileFields.value.every((file) => file.value.status === 'done'),
);

const allUploadsDoneOrError = computed(() =>
  fileFields.value.every((file) => file.value.status === 'done' || file.value.status === 'error'),
);

const totalSizePercent = computed(() => {
  return totalSize.value > 0 ? Math.round((totalUploaded.value / totalSize.value) * 100) : 0;
});

const classificationModel: Ref<DefaultClassification> | null = ref({
  pointOfSaleId: undefined,
  documentType: undefined,
  ticketId: undefined,
  isNewTicket: false,
  isClassificationNotApplicable: false,
  comment: '',
});

const onNewTicketCheck = (v: boolean, index: number, field: FieldEntry<UploadFileObject>) => {
  if (v) {
    field.value.isNewTicket = true;
    const newTicketId = generateTempTicket(index, field.value.file.name);
    field.value.ticketId = newTicketId;

    // Reset touched state for fresh start
    if (touched.value[index]) {
      touched.value[index].ticketId = false;
    }
  } else {
    // Uncheck new ticket - clear everything
    removeTempTicket(index);
    field.value.isNewTicket = false;
    field.value.ticketId = undefined;
  }
};

const onTicketIdUserUpdate = (
  e: SelectChangeEvent,
  index: number,
  field: FieldEntry<UploadFileObject>,
) => {
  // Mark as touched by user
  if (!touched.value[index]) touched.value[index] = {};
  touched.value[index].ticketId = true;

  field.value.isNewTicket = false;
  field.value.ticketId = e.value;
};

const updatePointOfSalesByNewTempTicket = (
  field: UploadFileObject,
  selectedPointOfSaleId: string | undefined,
) => {
  fileFields.value.forEach((ff) => {
    if (ff.value.ticketId === field.ticketId && !ff.value.isNewTicket) {
      ff.value.pointOfSaleId = selectedPointOfSaleId;
    }
  });
};

const onPointOfSaleUpdate = (v: string | undefined, field: FieldEntry<UploadFileObject>) => {
  const index = fileFields.value.findIndex((f) => f.key === field.key);
  if (index !== -1) {
    field.value.pointOfSaleId = v;
    if (field.value.isNewTicket) {
      updatePointOfSalesByNewTempTicket(field.value, v);
    }
  }
};

const onBatchTicketIdUpdate = (v: string | undefined) => {
  classificationModel.value.ticketId = v;
  if (v && ticketsAlreadyExisting.value.find((x) => x.id === v)?.version !== 0) {
    fileFields.value.forEach((file) => {
      file.value.isNewTicket = false;
    });
  }
};

const onDocumentTypeUpdate = (
  v: TicketDocumentV1Dto.documentType | undefined,
  field: FieldEntry<UploadFileObject>,
) => {
  const index = fileFields.value.findIndex((f) => f.key === field.key);
  if (index !== -1) {
    field.value.documentType = v;
  }
};

const onTicketIdUpdate = (v: string | undefined, field: FieldEntry<UploadFileObject>) => {
  const index = fileFields.value.findIndex((f) => f.key === field.key);
  if (index !== -1) {
    field.value.ticketId = v;
  }
};

const onFileSelect = async (event: any) => {
  const selected = event.files as File[];

  for (const file of selected) {
    const alreadyExists = values.files?.some(
      (f) => f.file.name === file.name && f.file.size === file.size,
    );

    if (!alreadyExists) {
      const isEncrypted = await isPdfEncrypted(file);

      const newFileObj: UploadFileObject = {
        file,
        progress: 0,
        status: 'pending',
        comment: '',
        pointOfSaleId: classificationModel.value.pointOfSaleId,
        documentType: classificationModel.value.documentType,
        ticketId: classificationModel.value.ticketId,
        isNewTicket: false,
        isEncrypted,
      };

      fieldArray.push(newFileObj);
      totalSize.value += file.size;
    }
  }
};

const uploadEvent = async (_uploadCallback: (this: void) => void) => {
  let hasError = false;
  const uploadedDocsThisRun: DocumentV1Dto[] = [];

  values.files.forEach((file, index) => {
    const hasPointOfSaleId = !!file.pointOfSaleId;
    const hasDocumentType = !!file.documentType;
    const hasTicketId = !!file.ticketId;

    const bothFilled = hasPointOfSaleId && hasDocumentType;
    const noneFilled = !hasPointOfSaleId && !hasDocumentType;

    if (hasTicketId && (!hasPointOfSaleId || !hasDocumentType)) {
      hasError = true;
      if (!hasPointOfSaleId) {
        form.setFieldError(`files[${index}].pointOfSaleId`, t('errors.required'));
      }
      if (!hasDocumentType) {
        form.setFieldError(`files[${index}].documentType` as const, t('errors.required'));
      }
    }

    if (!bothFilled && !noneFilled) {
      hasError = true;

      if (!hasPointOfSaleId) {
        form.setFieldError(`files[${index}].pointOfSaleId` as const, t('errors.required'));
      }
      if (!hasDocumentType) {
        form.setFieldError(`files[${index}].documentType` as const, t('errors.required'));
      }
    }
  });

  if (hasError) {
    return;
  }

  uploading.value = true;

  const fakeIds = ticketsAlreadyExisting.value.filter((x) => x.version === 0).map((x) => x.id);
  const fakeToRealTicketMap: Record<string, string | undefined> = {};
  for (const fakeId of fakeIds) fakeToRealTicketMap[fakeId] = undefined;

  const filesToUpload = fileFields.value
    .filter((f) => f.value.status !== 'done' && !f.value.isEncrypted)
    .sort((a, b) => (b.value.isNewTicket ? 1 : 0) - (a.value.isNewTicket ? 1 : 0));

  let newFileObj: FieldEntry<UploadFileObject> | undefined;

  for (const fileObj of filesToUpload) {
    newFileObj = undefined;

    const originalTicketId = fileObj.value.ticketId;
    const currentTicketId = originalTicketId
      ? (fakeToRealTicketMap[originalTicketId] ?? originalTicketId)
      : originalTicketId;

    newFileObj = { ...fileObj, value: { ...fileObj.value, ticketId: currentTicketId } };

    try {
      if (!newFileObj) {
        return;
      }

      newFileObj.value.status = 'uploading';
      const progressInterval = setInterval(() => {
        if (newFileObj && newFileObj.value.progress < 90) {
          newFileObj.value.progress += 10;
        }
      }, 200);

      const response = await uploadDocument({
        file: newFileObj.value.file,
        comment: newFileObj.value.comment,
        documentType: newFileObj.value.documentType,
        pointOfSaleId: newFileObj.value.pointOfSaleId,
        ticketId: newFileObj.value.ticketId,
        isNewTicket: newFileObj.value.isNewTicket,
      });

      clearInterval(progressInterval);
      newFileObj.value.progress = 100;
      newFileObj.value.status = 'done';
      newFileObj.value.response = response;
      totalUploaded.value += newFileObj.value.file.size;
      uploadedDocsThisRun.push(response);

      const realId = response.ticketToDocuments?.[0]?.id;
      if (realId && originalTicketId) {
        fakeToRealTicketMap[originalTicketId] = realId;
      }
    } catch (err: any) {
      const match = `${err}`?.match(/"message":\s*"([^"]+)"/);
      const message = match ? match[1] : 'Unbekannter Fehler';
      eventBus.emit('error500', { message });
      newFileObj.value.response = { error: message } as DocumentV1Dto & { error?: string };
      newFileObj.value.status = 'error';
      totalUploaded.value += fileObj.value.file.size;
    }

    const index = fileFields.value.findIndex((f) => f.value.file === fileObj.value.file);
    if (index !== -1) {
      fileFields.value[index] = newFileObj;
    }
  }

  uploading.value = false;

  if (uploadedDocsThisRun.length) {
    const ids = uploadedDocsThisRun.map((d) => d.id).filter(Boolean);
    emit('uploaded', { ids, documents: uploadedDocsThisRun });
    eventBus.emit('documents:uploaded', uploadedDocsThisRun);
  }
};

const clearUploadedFiles = () => {
  form.resetForm({
    values: {
      files: [],
    },
  });
  totalSize.value = 0;
  totalUploaded.value = 0;
};

const fileUploadRef = ref();

const chooseFiles = () => {
  fileUploadRef.value?.choose?.();
};

const startUpload = () => {
  void uploadEvent(() => fileUploadRef.value?.upload?.());
};

const clearFiles = () => {
  clearUploadedFiles();
  fileUploadRef.value?.clear?.();
};

defineExpose({
  chooseFiles,
  startUpload,
  clearFiles,
  pendingFiles,
  hasFiles: computed(() => values.files?.length > 0),
});

watch(
  () => classificationModel.value.pointOfSaleId,
  (newValue) => {
    if (newValue !== undefined && newValue !== null) {
      fileFields.value.forEach((file, index) => {
        // Only update if user hasn't manually changed this field
        if (!touched.value[index]?.pointOfSaleId) {
          file.value.pointOfSaleId = newValue;
        }
      });
    }
  },
);

watch(
  () => classificationModel.value.documentType,
  (newValue) => {
    if (newValue !== undefined && newValue !== null) {
      fileFields.value.forEach((file, index) => {
        // Only update if user hasn't manually changed this field
        if (!touched.value[index]?.documentType) {
          file.value.documentType = newValue;
        }
      });
    }
  },
);

watch(
  () => classificationModel.value.ticketId,
  (newValue) => {
    if (newValue !== undefined && newValue !== null) {
      fileFields.value.forEach((file, index) => {
        // Only update if user hasn't manually changed this field
        // Don't override if file has a temp ticket (isNewTicket is checked)
        if (!file.value.isNewTicket && !touched.value[index]?.ticketId) {
          file.value.ticketId = newValue;
        }
      });
    }
  },
);

watch(
  () => classificationModel.value.comment,
  (newValue) => {
    if (newValue !== undefined && newValue !== null) {
      fileFields.value.forEach((file) => {
        file.value.comment = newValue;
      });
    }
  },
);

watch(
  () => values.files,
  (newFiles) => {
    newFiles?.forEach((_file, index) => {
      form.setFieldError(`files[${index}].pointOfSaleId`, undefined);
      form.setFieldError(`files[${index}].documentType`, undefined);
    });
  },
  { deep: true },
);

// Watch for changes to defaultClassification and update classificationModel
// immediate: true ensures this runs before onFileSelect can be triggered
watch(
  () => props.defaultClassification,
  (newDefaults) => {
    if (newDefaults) {
      classificationModel.value = {
        ...classificationModel.value,
        ...newDefaults,
      };

      // Immediately propagate to any existing files using vee-validate API
      if (fileFields.value.length > 0) {
        fileFields.value.forEach((file, index) => {
          // Only overwrite if user hasn't touched this field
          if (newDefaults.pointOfSaleId && !touched.value[index]?.pointOfSaleId) {
            file.value.pointOfSaleId = newDefaults.pointOfSaleId;
          }
          if (newDefaults.documentType && !touched.value[index]?.documentType) {
            file.value.documentType = newDefaults.documentType;
          }
          if (newDefaults.ticketId && !file.value.isNewTicket && !touched.value[index]?.ticketId) {
            file.value.ticketId = newDefaults.ticketId;
          }
          if (newDefaults.comment !== undefined && newDefaults.comment !== null) {
            file.value.comment = newDefaults.comment;
          }
        });
      }
    }
  },
  { immediate: true, deep: true },
);
</script>

<style scoped lang="css">
@reference "tailwindcss";
@reference "tailwindcss/utilities";

@layer app {
  /* :deep() */
  .p-fileupload-header {
    @apply m-0 py-0;
    height: 0px;
    overflow: hidden;
  }

  .dropzone {
    @apply flex items-center justify-center flex-col;
  }

  .classify-comment-section .form-group {
    @apply px-0 m-0;
  }

  .classify-section-inline .form-group {
    @apply m-0 py-0;
  }
}
</style>
